import { useEffect, useState } from 'react'
import { Avatar } from '../components/Avatar'
import { fetchBuehneModus, type BuehnenModus } from '../lib/api'
import { getAudioContext, resumeAudioContext } from '../lib/audioContext'
import { GewinnspielPage } from './GewinnspielPage'
import { IntroPage, stilleWav } from './IntroPage'
import { PraesentationPage } from './PraesentationPage'

/** Wie oft der Bildschirm nachfragt, was er zeigen soll. */
const POLL_MS = 1000

/**
 * `/buehne`: **der** große Bildschirm für alles, was K4ia im Lab vorführt
 * (Flo, 08.10.). Was er zeigt, entscheidet das Handy mit der gemeinsamen
 * Fernbedienung (`/buehne/go/<token>`, BuehneRemotePage):
 *
 * - **Intro** und **Gespräch** — der Intro-Bildschirm (IntroPage): im Intro
 *   mit Begrüßung und Boxen, im Gespräch gleich der Chat.
 * - **Gewinnspiel** — der Bildschirm der Auslosung (GewinnspielPage).
 * - **Präsentation** — Folien mit K4ias Stimme (PraesentationPage). Die
 *   braucht den Schlüssel aus der Adresse im Admin (`/buehne?k=…`): Der
 *   Businessplan ist vertraulich, ohne Schlüssel gibt der Worker keine Folie her.
 *
 * Ein Klick zu Beginn gibt Ton und Vollbild frei; danach wechseln die
 * Ansichten ohne weiteren Klick (Chrome merkt sich die Geste für die Seite).
 * Beim Wechsel wird die alte Ansicht ausgehängt und hört auf zu sprechen.
 * `/intro` und `/gewinnspiel` gibt es weiter, jeweils für sich.
 */
export function BuehnePage({ schluessel = '' }: { schluessel?: string }) {
  const [bereit, setBereit] = useState(false)
  const [modus, setModus] = useState<BuehnenModus | null>(null)

  useEffect(() => {
    if (!bereit) return
    let aktiv = true
    const takt = () =>
      void fetchBuehneModus()
        .then((m) => {
          if (aktiv) setModus(m.modus)
        })
        .catch(() => undefined)
    takt()
    const id = setInterval(takt, POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [bereit])

  async function aktivieren() {
    // Im Klick: Ton für AudioContext und Audio-Elemente freigeben, Vollbild.
    const audio = new Audio(stilleWav())
    void audio.play().catch(() => undefined)
    getAudioContext()
    await resumeAudioContext().catch(() => undefined)
    void document.documentElement.requestFullscreen?.().catch(() => undefined)
    setBereit(true)
  }

  if (!bereit) {
    return (
      <button
        onClick={() => void aktivieren()}
        className="flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-bg p-12 text-center"
      >
        <Avatar state="idle" size={220} showLabel={false} />
        <div>
          <h1 className="text-3xl font-semibold">K4ia-Bühne</h1>
          <p className="mt-3 text-lg text-text-muted">
            Einmal klicken, um Ton und Vollbild freizugeben — danach steuert das Handy, ob hier das
            Intro, ein Gespräch, das Gewinnspiel oder die Präsentation läuft.
          </p>
        </div>
      </button>
    )
  }

  if (!modus) return <main className="min-h-screen bg-bg" data-testid="buehne-laedt" />

  return (
    <div data-testid="buehne" data-modus={modus}>
      {modus === 'gewinnspiel' ? (
        <GewinnspielPage key="gewinnspiel" autostart />
      ) : modus === 'praesentation' ? (
        <PraesentationPage key="praesentation" schluessel={schluessel} />
      ) : (
        // Intro und Gespräch teilen sich den Intro-Bildschirm — ein Wechsel
        // zwischen beiden hängt ihn nicht aus, das Handy schickt den Befehl.
        // Im Gespräch antwortet K4ia nur als Text, ohne Stimme.
        <IntroPage
          key="intro"
          autostart
          startInChat={modus === 'gespraech'}
          stumm={modus === 'gespraech'}
        />
      )}
    </div>
  )
}
